package com.eladfinish.bible.contest.screens.search.ui.dialogs

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Clear
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.ShapeDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.cancel
import com.eladfinish.bible.contest.colorAccent
import com.eladfinish.bible.contest.ui.components.dialogs.CustomTitle
import com.eladfinish.bible.contest.ui.components.loaders.CircularLoader
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar
import com.eladfinish.bible.contest.ui.layout.heightAboveKeyboard
import com.eladfinish.bible.contest.ui.layout.isKeyboardCoveringShortWindow
import dev.icerock.moko.resources.compose.colorResource
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.desc.desc

private const val AI_SEARCH_PROMPT_MAX_LENGTH = 300

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun AiSearchInputDialog(
    onDismiss: () -> Unit,
    onConfirm: (String) -> Unit,
    modifier: Modifier = Modifier,
    isLoading: Boolean = false,
    initialValue: String = "",
    maxLength: Int = AI_SEARCH_PROMPT_MAX_LENGTH,
) {
    BasicAlertDialog(
        onDismissRequest = {
            if (!isLoading) {
                onDismiss()
            }
        },
    ) {
        OutlinedCard(
            modifier = modifier
                .fillMaxWidth()
                .heightIn(max = 450.dp)
                .heightAboveKeyboard(),
            shape = ShapeDefaults.Medium,
            colors = CardDefaults.elevatedCardColors(),
            elevation = CardDefaults.elevatedCardElevation(),
        ) {
            val accentColor = colorResource(SharedRes.colors.colorAccent)
            val focusRequester = remember { FocusRequester() }

            val initialText = remember(initialValue, maxLength) {
                initialValue.take(maxLength)
            }

            var textFieldValue by rememberSaveable(initialText, stateSaver = TextFieldValue.Saver) {
                mutableStateOf(
                    TextFieldValue(
                        text = initialText,
                        selection = TextRange(initialText.length),
                    ),
                )
            }

            val trimmedText = remember(textFieldValue.text) {
                textFieldValue.text.trim()
            }

            val isInputValid = remember(trimmedText, isLoading) {
                trimmedText.isNotEmpty() && !isLoading
            }

            fun submit() {
                if (!isInputValid) return
                onConfirm(trimmedText)
            }

            // While the keyboard covers a short window only the field is left, in about 120dp.
            val isTyping = isKeyboardCoveringShortWindow()
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(if (isTyping) 8.dp else 16.dp),
            ) {
                val scrollState = rememberScrollState()

                Column(
                    modifier = Modifier
                        .weight(1f)
                        .mouseVerticalScrollbar(scrollState)
                        .verticalScroll(scrollState),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    if (!isTyping) CustomTitle(
                        text = "חיפוש חכם בתנ\"ך",
                    )

                    if (!isTyping) Text(
                        text = """כתבו בשפה חופשית מה תרצו לחפש, למשל:
                            |פסוקים שמכילים את שמות אבותינו - אברהם יצחק ויעקב, בסדר כלשהו.
                            |פסוקים שמכילים 3 מילים בדיוק.
                            |הפיצ'ר הזה עדיין בשלבי פיתוח, אנא דווחו על התרשמותכם.""".trimMargin(),
                        style = MaterialTheme.typography.bodyMedium,
                    )

                    OutlinedTextField(
                        modifier = Modifier
                            .fillMaxWidth()
                            .heightIn(min = if (isTyping) 0.dp else 120.dp)
                            .focusRequester(focusRequester),
                        value = textFieldValue,
                        onValueChange = { newValue ->
                            val truncatedText = newValue.text.take(maxLength)
                            val truncatedSelection = TextRange(
                                start = newValue.selection.start.coerceAtMost(truncatedText.length),
                                end = newValue.selection.end.coerceAtMost(truncatedText.length),
                            )
                            textFieldValue = newValue.copy(
                                text = truncatedText,
                                selection = truncatedSelection,
                            )
                        },
                        textStyle = MaterialTheme.typography.bodyLarge.copy(
                            fontSize = 16.sp,
                        ),
                        label = {
                            Text(
                                text = "מה לחפש?",
                                style = MaterialTheme.typography.bodySmall,
                            )
                        },
                        singleLine = false,
                        minLines = if (isTyping) 2 else 5,
                        maxLines = if (isTyping) 2 else 8,
                        enabled = !isLoading,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedLabelColor = accentColor,
                            cursorColor = accentColor,
                            focusedBorderColor = accentColor,
                        ),
                        keyboardOptions = KeyboardOptions.Default.copy(
                            capitalization = KeyboardCapitalization.Sentences,
                            imeAction = ImeAction.Default,
                        ),
                        trailingIcon = {
                            if (textFieldValue.text.isNotEmpty() && !isLoading) {
                                IconButton(
                                    onClick = {
                                        textFieldValue = TextFieldValue("")
                                    },
                                ) {
                                    Icon(
                                        imageVector = Icons.Filled.Clear,
                                        contentDescription = "Clear",
                                    )
                                }
                            }
                        },
                        supportingText = {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.End,
                            ) {
                                Text(
                                    text = "${textFieldValue.text.length}/$maxLength",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = if (textFieldValue.text.length >= maxLength) {
                                        MaterialTheme.colorScheme.error
                                    } else {
                                        MaterialTheme.colorScheme.onSurfaceVariant
                                    },
                                )
                            }
                        },
                    )
                }

                if (!isTyping) Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(32.dp),
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    if (isLoading) {
                        CircularLoader(
                            strokeWidth = 4.dp,
                            modifier = Modifier.size(32.dp),
                        )
                    }
                }

                if (!isTyping) Spacer(modifier = Modifier.height(8.dp))

                if (!isTyping) Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.End,
                ) {
                    OutlinedButton(
                        onClick = onDismiss,
                    ) {
                        Text(SharedRes.strings.cancel.desc().localized())
                    }

                    Spacer(modifier = Modifier.size(12.dp))

                    OutlinedButton(
                        onClick = ::submit,
                        enabled = isInputValid,
                    ) {
                        Text("אם לבינה תקרא")
                    }
                }
            }

            LaunchedEffect(Unit) {
                focusRequester.requestFocus()
                textFieldValue = textFieldValue.copy(
                    selection = TextRange(textFieldValue.text.length),
                )
            }
        }
    }
}

@Preview
@Composable
private fun AiSearchInputDialogPreview() {
    MaterialTheme {
        AiSearchInputDialog(
            onDismiss = {},
            onConfirm = {},
            isLoading = false,
            initialValue = "",
//            initialValue = "פסוקים על שלום וברכה באותו פסוק",
        )
    }
}

@Preview
@Composable
private fun AiSearchInputDialogLoadingPreview() {
    MaterialTheme {
        AiSearchInputDialog(
            onDismiss = {},
            onConfirm = {},
            isLoading = true,
            initialValue = "פסוקים שמתחילים בטוב ומסתיימים בשלום",
        )
    }
}
